import type { TFunction } from 'i18next';

import { DAYS, learningGoals, locations } from '@/data/partnerFinderData';
import type { DayOfWeek, MeetingPreference, RequestStatus } from '@/types/partner-finder';

const goalIdByLabel = new Map(
  learningGoals.map((goal) => [goal.label.trim().toLowerCase(), goal.id] as const),
);

const legacyGoalKeyByLabel: Record<string, string> = {
  'exam preparation': 'partnerFinder.search.goalExam',
  'conversation practice': 'partnerFinder.search.goalConversation',
  'business german': 'partnerFinder.search.goalBusiness',
  'writing practice': 'partnerFinder.search.goalWriting',
  'grammar practice': 'partnerFinder.form.goalGrammar',
  'general learning': 'partnerFinder.form.goalGeneral',
};

const locationByLabelOrId = new Map<string, string>();
locations.forEach((location) => {
  locationByLabelOrId.set(location.id.toLowerCase(), location.id);
  locationByLabelOrId.set(location.label.toLowerCase(), location.id);
  locationByLabelOrId.set(location.label.split(',')[0].trim().toLowerCase(), location.id);
});

export function translatePartnerGoal(t: TFunction, goal?: string | null): string {
  if (!goal) return '';
  const normalized = goal.trim().toLowerCase();
  const goalId = goalIdByLabel.get(normalized);
  if (goalId) {
    return t(`partnerFinder.values.goals.${goalId}`, { defaultValue: goal });
  }
  const legacyKey = legacyGoalKeyByLabel[normalized];
  if (legacyKey) {
    return t(legacyKey, { defaultValue: goal });
  }
  return goal;
}

export function translatePartnerLocation(t: TFunction, location?: string | null): string {
  if (!location) return '';
  const locationId = locationByLabelOrId.get(location.trim().toLowerCase());
  if (!locationId) return location;
  return t(`partnerFinder.values.locations.${locationId}`, { defaultValue: location });
}

export function translatePartnerDay(t: TFunction, day: DayOfWeek | string): string {
  if ((DAYS as readonly string[]).includes(day)) {
    return t(`partnerFinder.values.days.${day}`, { defaultValue: day });
  }
  return day;
}

export function translatePartnerDays(t: TFunction, days: readonly (DayOfWeek | string)[]): string {
  return days.map((day) => translatePartnerDay(t, day)).join(', ');
}

export function translateMeetingPreference(t: TFunction, preference: MeetingPreference): string {
  return t(`partnerFinder.values.meeting.${preference}`, {
    defaultValue: preference === 'online' ? 'Online' : 'In-person',
  });
}

export function translateRequestStatus(t: TFunction, status: RequestStatus): string {
  return t(`partnerFinder.status.${status}`, {
    defaultValue: status.charAt(0).toUpperCase() + status.slice(1),
  });
}
